import React, { useEffect, useState, useRef } from 'react';
import Table from '../../app/components/table/Table';
import { Link, useNavigate } from 'react-router-dom';
import { initialFilters, isAuthorized } from '../../utils/helper';
import { useGetAllCustomersQuery } from './customersApiSlice';
import { Col, Container, OverlayTrigger, Row } from 'react-bootstrap';
import CustomDropdown from '../../app/components/dropdown/CustomDropDown';
import DropDownIcon from '../../app/components/svgIcons/DropDownIcon';
import DropdownItem from 'react-bootstrap/DropdownItem';
import SearchInput from '../landingPage/components/SearchInput';
import { customerTypes } from './assets/customersUtil';
import DottedAction from '../../app/components/svgIcons/DottedAction';
import styled from 'styled-components';
import { StyledProduct } from '../products/assets/ProductStyled';
import { ShopStyled } from '../shopSetting/assets/shopStyled';
import CrossIconNew from '../../app/components/svgIcons/CrossIconNew';
import TickIcon from '../../app/components/svgIcons/TickIcon';
import CustomerReminder from './CustomerReminder';
import DownloadIcon from '../../app/components/svgIcons/DownloadIcon';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { StyledTooltip } from '../../app/components/styledComponents/StyledTooltip';

const StyledSpan = styled.span`
  color: #2a2a2a;
`;

const StyledDropTimeItem = styled(DropdownItem)`
  &:hover {
    color: #fff; /* Text color on hover */
  }
  &:active {
    color: #838383 !important; /* Text color when active */
  }
`;

const Customers = () => {
  const [openSideBar, setOpenSideBar] = useState(true);

  const [listing, setListing] = useState({
    ...initialFilters,
    sort: { path: 'first_name', order: 'asc' },
  });
  const [show, setShow] = useState(false);
  const navigation = useNavigate();
  const { data, isFetching, refetch } = useGetAllCustomersQuery({
    ...listing,
    sort: `${listing.sort.path},${listing.sort.order.toLowerCase()}`,
  });

  const token = useSelector((state) => state.auth.token); // Adjust the state path based on your store structure
  const [isLoading, setIsLoading] = useState(false);
  useEffect(() => {
    refetch();
  }, []);

  const [selectedCustomerType, setSelectedCustomerType] = useState({});
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const userPermissions = useSelector(
    (state) => state?.authorization?.userPermissions,
  );

  const has_permission_to_update_customer = isAuthorized(
    userPermissions,
    'can update customer',
  );

  const handleExport = async () => {
    setIsLoading(true);
    try {
      // Retrieve the token from the Redux store
      // Make the Axios request
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/customer/customer/downloadExcel`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: 'blob', // Important for handling binary data like CSV/XLSX files
        },
      );

      // Create a blob URL and download the file
      const url = window.URL.createObjectURL(new Blob([response.data])); // Use response.data for blob data
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'customers.xlsx'); // Modify the file name as needed
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link); // Clean up after download
    } catch (error) {
      console.error('Failed to export customers:', error);
    }
    setIsLoading(false);
  };

  const handlePerPageChange = async (size) => {
    setListing((prevState) => ({
      ...prevState,
      page: 0,
      size,
    }));
  };

  const handlePageChange = ({ selected }) => {
    setListing((prevState) => ({
      ...prevState,
      page: selected,
    }));
  };

  const column = [
    {
      id: 1,
      path: 'first_name',
      label: 'Customer Name',
      content: (customer) => (
        <div onClick={() => setOpenSideBar(false)}>{`${
          customer?.firstName || ''
        } ${customer?.lastName}`}</div>
      ),
    },
    {
      id: 2,
      path: 'company_name',
      label: 'Organization',
      content: (customer) => <div onClick={() => setOpenSideBar(false)}>-</div>,
    },
    {
      id: 3,
      path: 'total',
      label: 'Total Spend',
      content: ({ totalSpend }) => (
        <div onClick={() => setOpenSideBar(false)}>
          {totalSpend ? `$${totalSpend?.toFixed(2)}` : '-'}
        </div>
      ),
    },
    {
      id: 4,
      label: 'Contact',
      content: (customer) => (
        <div onClick={() => setOpenSideBar(false)}>
          <div>{customer?.email}</div>
          <div>{customer?.phone}</div>
        </div>
      ),
    },
    {
      id: 5,
      label: 'Reminder',
      alignBody: 'justify-content-center',
      alignHeader: 'justify-content-center',
      content: (customer) => {
        return (
          <div
            style={{
              cursor: customer?.emailReminder ? 'pointer' : 'not-allowed',
            }}
            onClick={() => setOpenSideBar(true)}
            className="d-flex align-items-center justify-content-center"
          >
            {customer?.emailReminder ? (
              <TickIcon />
            ) : (
              <>
                <CrossIconNew className="noReminderSvg" fill="red" />
                <style>
                  {`
        .noReminderSvg {
          cursor : not-allowed !important;
        }
              `}
                </style>
              </>
            )}
          </div>
        );
      },
    },
    {
      id: 6,
      label: 'Status',
      alignBody: 'justify-content-center',
      alignHeader: 'justify-content-center',
      content: (customer) => (
        <div
          onClick={() => setOpenSideBar(false)}
          className="d-flex align-items-center justify-content-center"
        >
          {!customer?.isActive ? (
            <>-</>
          ) : (
            <StyledActiveStatus
              className="rounded-2 p-2 px-3 px-md-4"
              isActive={customer?.isActive}
            >
              <h6 className="m-0">
                {customer?.isActive ? 'Active' : 'Inactive'}
              </h6>
            </StyledActiveStatus>
          )}
        </div>
      ),
    },
    {
      id: 7,
      isChecked: true,
      label: 'Actions',
      content: (customer) => (
        <CustomDropdown
          onClick={() => setOpenSideBar(false)}
          toggleClassName={`p-0 border-0 bg-transparent`}
          menuClassName={`table-dropdown `}
          dropdownToggle={
            <OverlayTrigger
              overlay={
                has_permission_to_update_customer ? (
                  <></>
                ) : (
                  <StyledTooltip>
                    You do not have permission to update customer details
                  </StyledTooltip>
                )
              }
            >
              <div
                className={`bg-transparent ${
                  has_permission_to_update_customer
                    ? 'cursor-pointer'
                    : 'cursor-not-allowed opacity-50'
                }`}
              >
                <DottedAction />
                <style>{`
              svg{
              cursor:${
                has_permission_to_update_customer
                  ? 'pointer !important'
                  : 'not-allowed !important'
              }
              }
              `}</style>
              </div>
            </OverlayTrigger>
          }
          dropdownMenu={
            <>
              {has_permission_to_update_customer && (
                <DropdownItem as={Link} to={`${customer.id}`}>
                  Edit
                </DropdownItem>
              )}
            </>
          }
        />
      ),
    },
  ];

  return (
    <>
      <ShopStyled.PageWrap isFullWidth>
        <Container fluid>
          <Row
            className="mx-0 my-3"
            style={{
              justifyContent: 'space-between',
            }}
          >
            <Col md={6} className="p-0">
              <div className="d-flex gap-3">
                <ShopStyled.EditButton
                  onClick={() => navigation('/customers/new')}
                  color={'primary'}
                  className={'mb-4 m-md-0 align-self-end align-self-md-auto'}
                  childrenProps={{ className: 'd-flex align-items-center' }}
                >
                  <ShopStyled.ButtonText>Add Customer</ShopStyled.ButtonText>
                </ShopStyled.EditButton>
                <CustomDropdown
                  toggleClassName={'table-dropdown bg-white ms-0'}
                  className="ms-0 custom-drop-down table-dropdown-custom"
                  dropdownToggle={
                    <StyledSpan className="flex-center w-100 justify-content-between new-font">
                      <ShopStyled.ButtonText className="me-2">
                        {selectedCustomerType?.label || 'All Customers'}
                      </ShopStyled.ButtonText>
                      <DropDownIcon rotate="down" size={'12px'} />
                    </StyledSpan>
                  }
                  dropdownMenu={
                    <>
                      {customerTypes?.map((item, index) => {
                        return (
                          <StyledDropTimeItem
                            key={`${item?.values}-${index}`}
                            onClick={() => setSelectedCustomerType(item || '')}
                          >
                            {item?.label}
                          </StyledDropTimeItem>
                        );
                      })}
                    </>
                  }
                />
              </div>
            </Col>
            <Col
              md={6}
              className="p-0 d-flex gap-3 align-items-center justify-content-end"
            >
              <SearchInput width="65%" placeholder="Search by customer name" />
              <ShopStyled.EditButton
                onClick={handleExport}
                color={'primary'}
                disabled={isLoading}
                loading={isLoading}
                className={'mb-4 m-md-0 align-self-end align-self-md-auto'}
                childrenProps={{ className: 'd-flex align-items-center' }}
              >
                <ShopStyled.ButtonText>
                  {' '}
                  <DownloadIcon /> Export{' '}
                </ShopStyled.ButtonText>
              </ShopStyled.EditButton>
            </Col>
          </Row>
          <StyledProduct.ProductsTable className="product-table">
            <Table
              className={'border-0'}
              columns={column}
              pointer={false}
              loading={isFetching}
              data={data?.objectData}
              rowHighlighter={(item) =>
                item.id === selectedCustomer?.id && openSideBar
              }
              onRowClick={(item) => {
                if (item?.emailReminder) {
                  setShow(true);
                  setSelectedCustomer(item);
                }
              }}
              highlightRowsOnHover={true}
              action={{
                handlePerPageChange,
                handlePageChange,
              }}
              sortColumn={listing.sort}
              onSort={(sort) => setListing((prev) => ({ ...prev, sort }))}
            />
          </StyledProduct.ProductsTable>

          {selectedCustomer && openSideBar && (
            <CustomerReminder
              show={show}
              onHide={() => {
                setShow(false);
                setSelectedCustomer(null);
              }}
              customer={selectedCustomer}
              handleModalAction={() => {}}
            />
          )}
        </Container>
      </ShopStyled.PageWrap>
    </>
  );
};

const StyledActiveStatus = styled.div`
  background-color: ${({ theme, isActive }) =>
    isActive ? theme.success : theme.inputBorder};

  h6 {
    color: ${({ theme, isActive }) =>
      isActive ? theme?.white : theme.textLightColor};
  }
`;
export default Customers;
